import React from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import { unescape } from 'lodash';
import LinkTranslated from '@components/shared/LinkTranslated';

/**
 * Transforms HTML nodes into React components.
 * Specifically, it replaces <a> or <anchor> tags with the LinkTranslated component.
 *
 * @param {object} node - The HTML node to be transformed.
 * @param {boolean} targetBlank - Should be target _blank or not.
 * @returns {JSX.Element | null} The transformed React component or null if the node is an empty link.
 */
export const transformNode = (node: any, targetBlank = false) => {
  if (node.type === 'tag' && (node.name === 'a' || node.name === 'anchor')) {
    if (node.children && node.children.length > 0 && node.children[0].data) {
      return (
        <LinkTranslated to={node?.attribs?.to || node?.attribs?.href} targetBlank={targetBlank || node?.attribs?.target === '_blank'}>
          {node.children[0].data}
        </LinkTranslated>
      );
    }
    return null;
  }
};

/**
 * Sanitizes an HTML string based on the allowIframe option.
 *
 * @param {string} html - The HTML string to be sanitized.
 * @param {boolean} allowIframe - Determines if iframes should be included.
 * @returns {string} The sanitized HTML string.
 */
export const sanitizeHtml = (html: string, allowIframe = false): string => {
  if (!DOMPurify?.sanitize) {
    console.warn('DOMPurify is not available for sanitization');
    return html.replace(/[<>]/g, ''); // Basic fallback sanitization
  }

  return DOMPurify.sanitize(html, {
    ADD_TAGS: allowIframe ? ['iframe'] : [],
    ADD_ATTR: allowIframe ? ['allow', 'allowfullscreen', 'frameborder', 'scrolling'] : []
  });
};

/**
 * Converts an HTML string to React components while transforming <a> tags to LinkTranslated components.
 *
 * @param {string} html - The HTML string to be transformed.
 * @param {boolean} [targetBlank=false] - Determines if links should open in a new tab.
 * @param {boolean} [allowIframe=false] - Determines if iframes should be included.
 * @returns {string | Element | Element[]} The resulting JSX element.
 */
export const getTranslatedHtml = (html: string, targetBlank: boolean = false, allowIframe: boolean = false) => {
  if (!html) {
    return '';
  }

  const cleanHtml = sanitizeHtml(html, allowIframe);

  return parse(cleanHtml, {
    replace: (node) => transformNode(node, targetBlank)
  });
};

export const unescapeDeep = (obj: any): any => {
  if (typeof obj === 'string') {
    return unescape(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(unescapeDeep);
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, unescapeDeep(value)]));
  }

  return obj;
};

export default {};
